// Faz BN — Tema modu context + provider.
// localStorage anahtarı: "miksan-theme"  (light | dark | system).
// "system" → prefers-color-scheme'i izler.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { buildMuiTheme } from "./muiTheme";
import { applyCssVars } from "./cssVars";

const STORAGE_KEY = "miksan-theme";
const ThemeModeContext = createContext({ mode: "light", preference: "light", setPreference: () => {}, toggle: () => {} });

function readStoredPreference() {
  if (typeof window === "undefined") return "light";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch { /* ignore */ }
  return "light";
}

function resolveSystemMode() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeModeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => readStoredPreference());
  const [systemMode, setSystemMode] = useState(() => resolveSystemMode());

  // prefers-color-scheme dinle (sadece "system" preference'da etkili)
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemMode(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const mode = preference === "system" ? systemMode : preference;

  // CSS variables'ı her mod değişiminde uygula
  useEffect(() => {
    applyCssVars(mode);
  }, [mode]);

  const setPreference = useCallback((p) => {
    setPreferenceState(p);
    try { window.localStorage.setItem(STORAGE_KEY, p); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    setPreference(mode === "dark" ? "light" : "dark");
  }, [mode, setPreference]);

  const muiTheme = useMemo(() => buildMuiTheme(mode), [mode]);

  const value = useMemo(() => ({ mode, preference, setPreference, toggle }), [mode, preference, setPreference, toggle]);

  return (
    <ThemeModeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
